import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { DashboardCourseCard } from "@/components/dashboard/DashboardCourseCard";
import { AppTopbar } from "@/components/layout/AppTopbar";
import { Sidebar } from "@/components/layout/Sidebar";
import {
  getCourseProgress,
  isPermissionExpired,
  useCoursePermissions,
  useUserCourseProgress,
} from "@/hooks/usePermissions";
import { useStripeRenewal } from "@/hooks/useCheckout";
import { useCreateCourseShare } from "@/hooks/useSharing";
import { useAuth } from "@/lib/auth";
import { buildShareTutorialUrl, copyToClipboard } from "@/lib/helpers";
import { useAppStore } from "@/lib/store";

const defaultInstructorImage =
  "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif";

export function DashboardPage() {
  const { user } = useAuth();
  const { data: permissions, loading, error } = useCoursePermissions();
  const { data: progress } = useUserCourseProgress();
  const { execute: createCourseShare } = useCreateCourseShare();
  const { execute: stripeRenewal, loading: renewalLoading, error: renewalError } = useStripeRenewal();
  const [extendingPermissionId, setExtendingPermissionId] = useState<string | null>(null);
  const { dashboardSearchInput, setDashboardSearchInput, clearCart } = useAppStore();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      clearCart();
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete("checkout");
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [clearCart, searchParams, setSearchParams]);

  const displayName =
    user?.user_metadata?.display_name ?? user?.email?.split("@")[0] ?? "User";

  const activePermissions = useMemo(
    () => (permissions ?? []).filter((permission) => !isPermissionExpired(permission)),
    [permissions],
  );

  const filteredPermissions = useMemo(() => {
    const query = dashboardSearchInput.trim().toLowerCase();

    if (!query) return activePermissions;

    return activePermissions.filter((permission) =>
      permission.course_id.name.toLowerCase().includes(query),
    );
  }, [activePermissions, dashboardSearchInput]);

  const handleShare = async (courseId: string) => {
    if (!user) return false;

    // Always go through the mutation so cached share data cannot copy a
    // stale/deleted token and existing links get their expiry refreshed.
    const result = await createCourseShare(courseId);
    const shareToken = result.data?.share_token;
    if (!shareToken) return false;

    return copyToClipboard(buildShareTutorialUrl(shareToken));
  };

  const handleExtend = (permissionId: string) => {
    setExtendingPermissionId(permissionId);
    void stripeRenewal(permissionId).finally(() => setExtendingPermissionId(null));
  };

  const dashboardGridClassName =
    "grid grid-cols-[repeat(auto-fill,minmax(min(26rem,100%),1fr))] items-start gap-[1.75rem] max-[767px]:gap-5 max-[479px]:gap-4";

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b] min-[992px]:flex-row">
      <Sidebar />

      <div className="min-w-0 flex-1">
        <AppTopbar
          title={displayName}
          subtitle="Welcome back to BollyRhythm👋🏻"
          searchValue={dashboardSearchInput}
          onSearchChange={setDashboardSearchInput}
        />

        <main className="px-[clamp(1rem,3vw,3rem)] py-12 max-[991px]:px-5 max-[991px]:py-8 max-[767px]:px-4 max-[767px]:py-5 max-[479px]:px-3.5 max-[479px]:py-4">
          <div className="sr-only" data-testid="dashboard-user-name">
            {displayName}
          </div>
          <div className="sr-only" data-testid="dashboard-active-count">
            {activePermissions.length}
          </div>

          {renewalError && (
            <section className="mb-[1.25rem] max-w-[31rem] rounded-[1rem] border border-[#ead4db] bg-[#fff7fa] p-[1rem] max-[479px]:rounded-[0.9rem] max-[479px]:p-[0.95rem]">
              <p className="text-[0.95rem] font-semibold text-[#8b2965]">
                Unable to start renewal checkout
              </p>
              <p className="mt-[0.35rem] text-[0.88rem] text-[#7c596b]">
                {renewalError}
              </p>
            </section>
          )}

          {loading ? (
            <section className={dashboardGridClassName}>
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="min-h-[23rem] animate-[app-skeleton-pulse_1.8s_ease-in-out_infinite] bg-[linear-gradient(180deg,#ffffff_0%,#f4f2f4_100%)]"
                />
              ))}
            </section>
          ) : error ? (
            <section className="max-w-[31rem] rounded-[1rem] border border-[#dfdbde] bg-[#fafafa] p-[1.35rem] max-[479px]:rounded-[0.9rem] max-[479px]:p-[1rem]">
              <h2 className="m-0 font-[family-name:var(--font-display)] text-[2rem] leading-[1.04] text-[#321b29] max-sm:text-[1.8rem] max-[479px]:text-[1.55rem]">
                Unable to load your library
              </h2>
              <p className="mt-[0.7rem] text-[0.96rem] text-[#70696f]">
                Refresh the page or try again in a moment.
              </p>
            </section>
          ) : filteredPermissions.length > 0 ? (
            <section
              className={dashboardGridClassName}
              data-testid="dashboard-owned-tutorials"
            >
              {filteredPermissions.map((permission) => (
                <DashboardCourseCard
                  key={permission.id}
                  permission={permission}
                  instructorImage={defaultInstructorImage}
                  performanceHref="/app/dashboard/home#performances"
                  onShare={handleShare}
                  onExtend={handleExtend}
                  extending={extendingPermissionId === permission.id}
                  progressPercent={
                    progress && user
                      ? getCourseProgress(progress, permission.course_id.id, user.id)
                      : 0
                  }
                />
              ))}
            </section>
          ) : dashboardSearchInput.trim() ? (
            <section className="max-w-[31rem] rounded-[1rem] border border-[#dfdbde] bg-[#fafafa] p-[1.35rem] max-[479px]:rounded-[0.9rem] max-[479px]:p-[1rem]">
              <h2 className="m-0 font-[family-name:var(--font-display)] text-[2rem] leading-[1.04] text-[#321b29] max-sm:text-[1.8rem] max-[479px]:text-[1.55rem]">
                No tutorials match this search
              </h2>
              <p className="mt-[0.7rem] text-[0.96rem] text-[#70696f]">
                Try a different search term.
              </p>
            </section>
          ) : (
            <section className="max-w-[31rem] rounded-[1rem] border border-[#dfdbde] bg-[#fafafa] p-[1.35rem] max-[479px]:rounded-[0.9rem] max-[479px]:p-[1rem]">
              <h2 className="m-0 text-[1.5rem] font-semibold leading-[1.2] text-[#321b29] max-sm:text-[1.3rem] max-[479px]:text-[1.2rem]">
                Your Library is Empty
              </h2>
              <p className="mt-[0.5rem] text-[0.875rem] leading-[1.5] text-[#70696f]">
                Browse our collection and add your first tutorial to get started.
              </p>
              <Link
                to="/dance-library"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-[1rem] bg-[#762a5c] px-5 py-4 text-[0.875rem] font-medium leading-none text-white whitespace-nowrap transition-transform duration-200 hover:-translate-y-[5%] active:scale-95 max-[479px]:w-full max-[479px]:px-4 max-[479px]:py-3.5"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
                  <path d="M9.25 9.25V4.75H10.75V9.25H15.25V10.75H10.75V15.25H9.25V10.75H4.75V9.25H9.25Z" fill="white" />
                </svg>
                <span>Get Tutorials</span>
              </Link>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
